// 3rd party libs
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';

// local imports
import { useAppSelector } from '../../../store/hooks';
import { GROUP_WITH_SUBGROUP, ORGANIZATION, USER } from '../../../enums/TYPES';
import UsersIcon from '../../../assets/icons/users.svg';
import TreeIcon from '../../../assets/icons/tree.svg';
import { DateFormatter } from '../../../helpers';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { updateGroupUsers } from '../../../api';
import LinearProgress from '../../modules/LinearProgress';
import { NOTIFICATION } from '../../../enums';
import { PATHS } from '../../../enums/PATHS';
import { ROLES } from '../../../enums/ROLES';
import styles from './OrgGroupCard.module.scss';
import TrashButton from '../../modules/TrashButton';
import EditButton from '../../modules/EditButton';

type TOrgGroupCard = {
  data: GROUP_WITH_SUBGROUP;
  onEdit: (group: GROUP_WITH_SUBGROUP) => void;
  toDelete?: (id: string) => void;
  setFetchTrigger: (num: number) => void;
};

const OrgGroupCard: React.FC<TOrgGroupCard> = ({ data, onEdit, toDelete, setFetchTrigger }) => {
  const history = useHistory();

  const userRole = useAppSelector((state) => state.user.role);
  const [hover, setHover] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data.title);
  const ref = useRef<any>();

  const [orgs, setOrgs] = useState<ORGANIZATION[]>([]);

  useEffect(() => {
    if (data.organizations) {
      setOrgs(data.organizations);
    }
  }, [data]);

  const notification = useSnackbar();

  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isEditing && ref.current && !ref.current.contains(e.target)) {
        setIsEditing(false);
        setHover(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isEditing]);

  const onDelete = () => {
    if (toDelete) {
      toDelete(data.id);
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    onEdit(data);
  };

  const [loading, setLoading] = useState(false);
  const handleTitleSubmit = async (e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault();

    if (title === data.title) {
      setHover(false);
      setIsEditing(false);
      return;
    } else if (title.trim().length === 0) {
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      setTitle(data.title);
      setHover(false);
      setIsEditing(false);
      return;
    }

    setIsEditing(false);

    try {
      setLoading(true);
      await updateGroupUsers({
        title,
        groupdId: data.id,
      });

      setFetchTrigger(Date.now());
      notification.showMessage(NOTIFICATION.UPDATED);

      setHover(false);
    } catch (error) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }

      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleTitleEdit = () => {
    if (userRole === ROLES.ORGANIZATION_ADMIN) {
      setIsEditing(!isEditing);
    } else {
      history.push(`${PATHS.SUB_GROUPS_MANAGEMENT.path}${data.id}`, data.id);
    }
  };

  const onRedirect = (e: React.MouseEvent<HTMLDivElement>) => {
    // if (e.currentTarget.contains(e.target as Node) && [ROLES.ORGANIZATION_ADMIN, ROLES.ANALYST, ROLES.VIEWER].includes(userRole!)) {
    //   history.push(`${PATHS.SUB_GROUPS_MANAGEMENT.path}${data.id}`, data.id);
    // }
  };

  return (
    <div className={styles.card} ref={ref} onClick={onRedirect} onContextMenu={handleEditClick}>
      {isEditing ? (
        <div className={styles.inputContainer}>
          <form
            onClick={(e) => {
              e.stopPropagation();
            }}
            onSubmit={handleTitleSubmit}
          >
            <input className={styles.input} onChange={handleTitleChange} defaultValue={data.title} onBlur={handleTitleEdit} />
          </form>
        </div>
      ) : (
        <div className={styles.title} onClick={onRedirect}>
          <span onClick={onRedirect}>{data.title}</span>
          {loading && <LinearProgress />}
        </div>
      )}

      <div className={styles.details} onClick={onRedirect}>
        <Tooltip
          placement="right"
          arrow
          title={
            <div onClick={onRedirect} className={styles.tooltip}>
              {' '}
              {orgs.map((u) => (
                <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  {' '}
                  <span style={{ marginRight: '8px' }}>{u?.name} </span> <span> {u?.location}</span>
                </div>
              ))}{' '}
            </div>
          }
        >
          <div className={styles.data} onClick={onRedirect}>
            <img src={FolderIcon} alt="users" />
            {orgs.length}
          </div>
        </Tooltip>
      </div>
      <div
        className={styles.created}
        style={{
          width: userRole === ROLES.ORGANIZATION_ADMIN ? '20%' : '32%',
          justifyContent: userRole === ROLES.ORGANIZATION_ADMIN ? 'start' : 'end',
        }}
        onClick={onRedirect}
      >
        {DateFormatter(data.created_at, true)}
      </div>
      <div
        className={styles.updated}
        style={{
          width: userRole === ROLES.ORGANIZATION_ADMIN ? '20%' : '33%',
          justifyContent: userRole === ROLES.ORGANIZATION_ADMIN ? 'start' : 'end',
        }}
        onClick={onRedirect}
      >
        {data?.updated_at && DateFormatter(data?.updated_at, true)}
      </div>

      {userRole === ROLES.ORGANIZATION_ADMIN && (
        <div className={styles.action}>
          <EditButton onClick={handleEditClick} />
          <TrashButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default OrgGroupCard;
